import type { PostgrestError, SupabaseClient } from '@supabase/supabase-js';
import { getSupabaseClient } from './supabase';
import { fetchLocations } from './locations';
import { findEventByCode } from './events';
import { eventCreationDateErrors } from '../domain/events/creationDates';

type ClientOption = { supabase?: SupabaseClient };

export type SiteOption = { location_id: string; name: string; customer_pickup_enabled: boolean };
export type VenueOption = {
  id: string;
  venue_name: string;
  city?: string | null;
  state?: string | null;
  is_outdoor?: boolean | null;
  is_ita_internal?: boolean | null;
};
export type VenueRoomTemplate = { id: string; name: string; type?: string | null; capacity?: number | null };

export async function fetchSiteOptions(
  allowedLocationIds: string[] | null | undefined,
  options?: ClientOption,
): Promise<{ data: SiteOption[]; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await fetchLocations(allowedLocationIds, { supabase });
  if (error || data.length === 0) return { data: [], error };
  const locationIds = data.map(location => location.location_id);
  const settingsResult = await supabase
    .from('location_settings')
    .select('location_id,customer_pickup_enabled')
    .in('location_id', locationIds);
  if (settingsResult.error) return { data: [], error: settingsResult.error };
  const enabledByLocation = new Map(
    (settingsResult.data ?? []).map(settings => [String(settings.location_id), settings.customer_pickup_enabled === true]),
  );
  return {
    data: data.map(location => ({
      location_id: location.location_id,
      name: location.name,
      customer_pickup_enabled: enabledByLocation.get(location.location_id) === true,
    })),
    error: null,
  };
}

export async function searchVenues(
  term: string,
  options?: ClientOption,
): Promise<{ data: VenueOption[]; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const query = supabase
    .from('venues')
    .select('id,venue_name,city,state,is_outdoor,is_ita_internal')
    .ilike('venue_name', `%${term}%`)
    .is('deleted_at', null)
    .limit(25)
    .order('venue_name');
  const { data, error } = await query;
  return { data: (data as VenueOption[]) || [], error: error ?? null };
}

export async function fetchVenueRooms(
  venueId: string,
  options?: ClientOption,
): Promise<{ data: VenueRoomTemplate[]; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase
    .from('venue_rooms')
    .select('id,name,type,capacity')
    .eq('venue_id', venueId)
    .order('position');
  return { data: (data as VenueRoomTemplate[]) || [], error: error ?? null };
}

export async function fetchDefaultVenueForSite(
  siteId: string,
  options?: ClientOption,
): Promise<{ data: VenueOption | null; error: PostgrestError | null }> {
  if (!siteId) return { data: null, error: null };
  const supabase = options?.supabase ?? getSupabaseClient();
  const ls = await supabase
    .from('location_settings')
    .select('master_billing_enabled')
    .eq('location_id', siteId)
    .maybeSingle();
  if (ls.error) return { data: null, error: ls.error };
  const master = !!(ls.data as any)?.master_billing_enabled;
  if (!master) return { data: null, error: null };
  const res = await supabase
    .from('location_venues')
    .select('venue_id, venues!inner(id,venue_name,city,state,is_outdoor,is_ita_internal)')
    .eq('location_id', siteId)
    .eq('is_default', true)
    .maybeSingle();
  if (res.error) return { data: null, error: res.error };
  const v = (res.data as any)?.venues;
  if (!v) return { data: null, error: null };
  return {
    data: {
      id: v.id,
      venue_name: v.venue_name,
      city: v.city ?? null,
      state: v.state ?? null,
      is_outdoor: v.is_outdoor ?? null,
      is_ita_internal: v.is_ita_internal ?? null,
    },
    error: null,
  };
}

export async function isVenueLinkedToSite(
  siteId: string,
  venueId: string,
  options?: ClientOption,
): Promise<{ linked: boolean; confirmsItaContract: boolean | null; error: PostgrestError | null }> {
  if (!siteId || !venueId) return { linked: false, confirmsItaContract: null, error: null };
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase
    .from('location_venues')
    .select('location_id,venue_id,is_default,confirms_ita_contract')
    .eq('location_id', siteId)
    .eq('venue_id', venueId)
    .maybeSingle();
  if (error) return { linked: false, confirmsItaContract: null, error };
  return {
    linked: !!data && !!(data as any).is_default,
    confirmsItaContract: data ? (data as any).confirms_ita_contract !== false : null,
    error: null,
  };
}

export async function setVenueDefaultForSite(
  siteId: string,
  venueId: string,
  next: boolean,
  confirmsItaContract = true,
  options?: ClientOption,
): Promise<PostgrestError | null> {
  if (!siteId || !venueId) return null;
  const supabase = options?.supabase ?? getSupabaseClient();
  if (next) {
    const { error: clearError } = await supabase
      .from('location_venues')
      .update({ is_default: false })
      .eq('location_id', siteId)
      .eq('is_default', true);
    if (clearError) return clearError;
    const { error } = await supabase.rpc('link_location_venue', {
      p_location_id: siteId,
      p_venue_id: venueId,
      p_is_default: true,
      p_confirms_ita_contract: confirmsItaContract,
    });
    return error ?? null;
  }
  const { error } = await supabase
    .from('location_venues')
    .update({ is_default: false })
    .eq('location_id', siteId)
    .eq('venue_id', venueId);
  return error ?? null;
}

export async function resolveLocationForVenue(
  venueId: string,
  options?: ClientOption,
): Promise<{ locationId: string | null; error: PostgrestError | null }> {
  if (!venueId) return { locationId: null, error: null };
  const supabase = options?.supabase ?? getSupabaseClient();
  // 1. Check contracted_location_id on venue
  const { data: venue, error: vErr } = await supabase
    .from('venues')
    .select('contracted_location_id')
    .eq('id', venueId)
    .maybeSingle();
  if (vErr) return { locationId: null, error: vErr };
  if ((venue as any)?.contracted_location_id) {
    return { locationId: (venue as any).contracted_location_id, error: null };
  }
  // 2. Fall back to location_venues (prefer is_default)
  const { data: lv, error: lvErr } = await supabase
    .from('location_venues')
    .select('location_id')
    .eq('venue_id', venueId)
    .order('is_default', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (lvErr) return { locationId: null, error: lvErr };
  return { locationId: (lv as any)?.location_id ?? null, error: null };
}

export async function fetchNextOrderRefForSite(
  siteId: string,
  options?: ClientOption,
): Promise<string | null> {
  if (!siteId) return null;
  const supabase = options?.supabase ?? getSupabaseClient();
  const { fetchNextOrderRef } = await import('../utils/codeGenerators');
  return fetchNextOrderRef(supabase, siteId);
}

export async function releaseOrderRefHold(
  hold: { code: string; locationId: string },
  options?: ClientOption,
): Promise<void> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { releaseOrderRef } = await import('../utils/codeGenerators');
  await releaseOrderRef(supabase, hold.locationId, hold.code);
}

export async function consumeOrderRefHold(
  hold: { code: string; locationId: string },
  options?: ClientOption,
): Promise<void> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { consumeOrderRef } = await import('../utils/codeGenerators');
  await consumeOrderRef(supabase, hold.locationId, hold.code);
}

export async function checkEventCodeExists(
  code: string,
  excludeEventId?: string | null,
  options?: ClientOption,
): Promise<{ id: string; name: string | null } | null> {
  const supabase = options?.supabase ?? getSupabaseClient();
  return findEventByCode(code, excludeEventId, { supabase });
}

export type RoomDraft = { name: string; type?: string | null; capacity?: number | null; templateId?: string | null };

export type EventCreationPayload = {
  name: string;
  event_code: string;
  show_start_date: string | null;
  show_end_date: string | null;
  event_start_date: string | null;
  event_end_date: string | null;
  fill_date?: string | null;
  fill_time?: string | null;
  ship_date?: string | null;
  ship_time?: string | null;
  return_date?: string | null;
  return_time?: string | null;
  site_location_id: string | null;
  customer_pickup?: boolean;
  client_id?: string | null;
};

export type EventLineCreationPayload = {
  itemId?: string | null;
  productId?: string | null;
  description?: string | null;
  qty?: number | null;
  chargeDays?: number | null;
  unitPrice?: number | null;
  usageRoomDate?: string | null;
  itemCode?: string | null;
  productCode?: string | null;
  note?: string | null;
};

export async function createEventBundle(params: {
  event: EventCreationPayload;
  venueSelection?: { venueId?: string | null; createVenue?: { venue_name: string; city?: string | null; state?: string | null; is_outdoor?: boolean | null } | null };
  rooms: RoomDraft[];
  eventLines?: EventLineCreationPayload[];
  supabase?: SupabaseClient;
}): Promise<{ event: any; error: PostgrestError | null }> {
  const { event, venueSelection, rooms, eventLines } = params;
  // Reject an incomplete window before any event, venue, room, or day is written.
  const dateErrors = eventCreationDateErrors(event.event_start_date, event.event_end_date);
  if (dateErrors.start || dateErrors.end) throw new Error(dateErrors.start || dateErrors.end);
  const supabase = params.supabase ?? getSupabaseClient();
  
  // Get current user's profile ID for created_by
  const { data: profileId } = await supabase.rpc('current_user_profile_id').maybeSingle();
  
  const eventPayload = {
    ...event,
    created_by: profileId || null,
  };
  
  const { data: ev, error: evErr } = await supabase
    .from('events')
    .insert(eventPayload)
    .select(
      'id,name,event_code,show_start_date,show_end_date,event_start_date,event_end_date,status,fill_date,fill_time,ship_date,ship_time,return_date,return_time',
    )
    .single();
  if (evErr || !ev) return { event: null, error: evErr ?? null };
  let venueId = venueSelection?.venueId ?? undefined;
  if (!venueId && venueSelection?.createVenue) {
    const { data: v, error: vErr } = await supabase
      .from('venues')
      .insert({
        venue_name: venueSelection.createVenue.venue_name,
        city: venueSelection.createVenue.city ?? null,
        state: venueSelection.createVenue.state ?? null,
        is_outdoor: venueSelection.createVenue.is_outdoor ?? null,
      })
      .select('id,venue_name')
      .single();
    if (vErr) return { event: null, error: vErr };
    venueId = v?.id;
  }
  let eventVenueId: string | undefined;
  if (venueId) {
    const { error: linkErr } = await supabase.from('event_venues').insert({ event_id: ev.id, venue_id: venueId });
    if (linkErr) return { event: null, error: linkErr };
    const { data: evv } = await supabase
      .from('event_venues')
      .select('id')
      .eq('event_id', ev.id)
      .eq('venue_id', venueId)
      .maybeSingle();
    eventVenueId = (evv as any)?.id;
  }
  if (rooms.length && eventVenueId) {
    const sourceIds: Array<string | null> = new Array(rooms.length).fill(null);
    for (let i = 0; i < rooms.length; i += 1) {
      const r = rooms[i];
      if (r.templateId) {
        sourceIds[i] = r.templateId;
        continue;
      }
      if (!venueId) {
        sourceIds[i] = null;
        continue;
      }
      const insert: any = { venue_id: venueId, name: r.name, type: r.type ?? null, capacity: r.capacity ?? null };
      const { data: vroom, error: vErr } = await supabase.from('venue_rooms').insert(insert).select('id').single();
      if (vErr) return { event: null, error: vErr };
      sourceIds[i] = (vroom as any)?.id || null;
    }
    // position was omitted, so wizard-created rooms landed with a null ordinal
    // while rooms added through other flows carry one. That left their ordering
    // dependent on insertion/query order rather than the sidebar sequence.
    const payload = rooms.map((r, idx) => ({
      event_id: ev.id,
      event_venue_id: eventVenueId,
      name: r.name,
      type: r.type || null,
      capacity: r.capacity || null,
      source_venue_room_id: sourceIds[idx],
      position: idx + 1,
    }));
    const { error: roomErr } = await supabase.from('event_rooms').insert(payload);
    if (roomErr) return { event: null, error: roomErr };
  }
  const startDate = event.event_start_date || event.show_start_date;
  const endDate = event.event_end_date || event.show_end_date || startDate;
  if (startDate) {
    const start = startDate;
    const end = endDate || start;
    const eventDays: string[] = [];
    const parse = (d: string) => {
      const [y, m, d2] = d.split('-').map((n) => Number(n));
      return new Date(Date.UTC(y, m - 1, d2));
    };
    const s = parse(start);
    const e = parse(end);
    const cursor = new Date(s.getTime());
    while (cursor.getTime() <= e.getTime()) {
      eventDays.push(cursor.toISOString().slice(0, 10));
      cursor.setUTCDate(cursor.getUTCDate() + 1);
    }
    if (eventDays.length) {
      const rows = eventDays.map((day, idx) => ({ event_id: ev.id, event_day: day, position: idx }));
      const { error: daysErr } = await supabase.from('event_days').insert(rows);
      if (daysErr) return { event: null, error: daysErr };
    }
  }

  const seedLines = Array.isArray(eventLines) ? eventLines : [];
  if (seedLines.length > 0) {
    const fallbackUsageDate = startDate || new Date().toISOString().slice(0, 10);
    const linePayload = seedLines.map((line, index) => {
      const qtyValue = Number(line.qty ?? 1);
      const chargeDaysValue = Number(line.chargeDays ?? 0);
      const unitPriceValue = Number(line.unitPrice ?? 0);
      return {
        event_id: ev.id,
        item_id: line.itemId || null,
        product_id: line.productId || null,
        description: line.description || null,
        qty: Number.isFinite(qtyValue) && qtyValue > 0 ? Math.round(qtyValue) : 1,
        charge_days: Number.isFinite(chargeDaysValue) && chargeDaysValue >= 0 ? chargeDaysValue : 0,
        unit_price: Number.isFinite(unitPriceValue) ? unitPriceValue : 0,
        usage_room_date: line.usageRoomDate || fallbackUsageDate,
        line_number: index + 1,
        room_key: 'GENERAL',
        item_code: line.itemCode || null,
        product_code: line.productCode || null,
        note: line.note || null,
      };
    });
    const { error: eventLinesError } = await supabase.from('event_lines').insert(linePayload);
    if (eventLinesError) return { event: null, error: eventLinesError };
  }

  return { event: ev, error: null };
}
